import React from "react";
import {connect, useSelector} from 'react-redux'
import { push } from 'react-router-redux'
import { List, Avatar, Space, Typography, Tooltip, Row, Col, Badge, Tag, Checkbox } from "antd";
import {
  ClockCircleOutlined,
  LikeOutlined,
  MessageOutlined,
  PhoneOutlined,
  RobotOutlined,
  RollbackOutlined,
  SoundOutlined,
  StarFilled,
  StarOutlined,
  StopOutlined,
  SwapLeftOutlined,
} from "@ant-design/icons";
import { getActiveTime, extractMessageText } from "../../helpers/ui";
import ViewerAvatarGroup from "../../../../common/ViewerAvatarGroup";
import { useStore } from '../../../inbox-store'
import { useFeature } from '../../../../common/FeatureGate';
import ChannelIcon from '../../../../common/ChannelIcon';
import { normaliseChannel } from '../../../../../utils/channels';
const { Text, Title } = Typography;

// ADR-025: list rows carry the conversation's transport as `channel_type`.
const SNIPPET_CHANNELS = ['sms', 'whatsapp', 'webchat', 'messenger'];

const IconText = ({ icon, text }: { icon: React.FC; text: string }) => (
  <Space>
    {React.createElement(icon)}
    {text}
  </Space>
);

export const smallAvatarText: React.CSSProperties = {
  color: 'white',
  fontSize: "11px",
  alignItems: "center",
  justifyContent: "center",
  display: "grid",
};

/**
 * GoHire Conversation List Styles
 * States: Read, Unread, Selected (activeContact)
 * Using 'normal' and 'bold' keywords for consistent font rendering
 */
const conversationStyles = {
  // Read state (default)
  read: {
    row: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E6E6E6',
      padding: '6px 8px',
    },
    name: {
      color: '#263238',
      fontWeight: 'normal' as const,
      fontSize: '14px',
    },
    snippet: {
      color: '#607D8B',
      fontSize: '13px',
    },
    timestamp: {
      color: '#607D8B',
      fontSize: '12px',
    },
  },
  // Unread state - same font styling as selected
  unread: {
    row: {
      backgroundColor: '#F2F8F4',
      borderBottom: '1px solid #E6E6E6',
      padding: '6px 8px',
    },
    name: {
      color: '#2E7D32',
      fontWeight: 500,
      fontSize: '14px',
    },
    snippet: {
      color: '#455A64',
      fontSize: '13px',
    },
    timestamp: {
      color: '#607D8B',
      fontSize: '12px',
    },
  },
  // Selected/Active state
  selected: {
    row: {
      backgroundColor: '#E8F5E9',
      borderBottom: '1px solid #E6E6E6',
      position: 'relative' as const,
      padding: '6px 8px',
    },
    leftBar: {
      position: 'absolute' as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: '#4CAF50',
    },
    name: {
      color: '#2E7D32',
      fontWeight: 600,
      fontSize: '14px',
    },
    snippet: {
      color: '#455A64',
      fontSize: '13px',
    },
    timestamp: {
      color: '#607D8B',
      fontSize: '12px',
    },
  },
  // Hover state (applied via CSS)
  hover: {
    backgroundColor: '#F5FBF6',
  },
};

// Viewer avatar logic moved to ViewerAvatarGroup

const MessageListItem = ({item, goToContactDetails, latestNotifications = []}:any) => {

  const activeContact = useStore((state)=>state.activeContact)
  const setActiveContact = useStore((state)=>state.setActiveContact)
  // Bulk-select (multi-select → Add to group / Add tag). Gated on the
  // "Contacts & Groups" feature, which owns groups and tags.
  const canBulkSelect = useFeature('contacts');
  const isChecked = useStore((state) => state.selectedContacts.has(item.contact_id));
  const toggleContactSelected = useStore((state) => state.toggleContactSelected);

  // Determine conversation state
  const isSelected = activeContact === item.contact_id;
  // Mark unread based on the server-provided flag `hasUnreadNotification`
  const isUnread = !isSelected && Boolean(item.hasUnreadNotification);
  
  // Get the appropriate styles based on state
  const getStyles = () => {
    if (isSelected) return conversationStyles.selected;
    if (isUnread) return conversationStyles.unread;
    return conversationStyles.read;
  };
  
  const styles = getStyles();

  return <List.Item
  onClick={(e)=>
    {
    if(item.contact_id !== activeContact){
      setActiveContact(item.contact_id)
    }
  }}
    className={`gh-conversation-row ${isSelected ? 'gh-selected' : ''} ${isUnread ? 'gh-unread' : ''} ${isChecked ? 'gh-checked' : ''}`}
    data-contact-id={item.contact_id}
    style={{
      ...styles.row,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      paddingLeft: isSelected ? 10 : 6,
      // main.css forces `.scrollableListDiv .ant-list-item` into a column;
      // the checkbox needs a row so it sits left of the content.
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }}
    key={item.contact_id}
  >
    {/* Left bar for selected state */}
    {isSelected && (
      <div style={conversationStyles.selected.leftBar} />
    )}

    {/* Bulk-select checkbox. The wrapper swallows the click so the row's
        onClick (which opens the conversation) does not fire. */}
    {canBulkSelect && (
      <span
        className="gh-conversation-checkbox"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginRight: 8 }}
      >
        <Checkbox
          checked={isChecked}
          aria-label={`Select ${item.name || 'contact'}`}
          onChange={() => toggleContactSelected({ contact_id: item.contact_id, name: item.name })}
        />
      </span>
    )}

    <List.Item.Meta
      style={{
        textAlign: "start",
        flex: 1,
        minWidth: 0,
      }}
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: 4,
          }}
        >
           <span
              onClick={(e) => {
                e.stopPropagation(); // prevent parent click
                goToContactDetails(item.contact_id)();
              }}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
            >
                {isUnread && (
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#4CAF50',
                      flexShrink: 0,
                    }}
                  />
                )}
                {item.star && (
                  <StarFilled style={{ color: 'gold', fontSize: 12 }} />
                )}
                {item.blacklist && (
                  <Tooltip title="Opted out" placement="top">
                    <StopOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                  </Tooltip>
                )}
                <Text ellipsis style={{ margin: 0, ...styles.name }}>
                  {item.name}
                </Text>
          </span>
          {/* Prefer interactionDetails (rich: type/id/name/avatar_url). Backend
              now returns interactions as string[] for v1-client compatibility
              and the rich shape as interactionDetails. */}
          <ViewerAvatarGroup viewers={item.interactionDetails || item.interactions || []} />
        </div>
      }
      description={
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ display: 'block', ...styles.snippet }} ellipsis={true}>
                {(() => {
                  const ch = normaliseChannel(item.channel_type);
                  return ch && SNIPPET_CHANNELS.includes(ch)
                    ? <ChannelIcon channel={ch} size={12} style={{ opacity: 0.7, marginRight: 4 }} />
                    : null;
                })()}
                {extractMessageText(item.message)}
              </Text>
            </div>
            <Space style={{ padding: '0 4px' }}>
              <ClockCircleOutlined style={{ color: "#c5c5c5" }} />
              <Text
                type="secondary"
                style={{ fontSize: "11px", ...styles.timestamp }}
              >
                {getActiveTime(item.created_at)}
              </Text>
            </Space>
          </div>
          {((item.contactTagValues && item.contactTagValues.length > 0) || (item.contactGroups && item.contactGroups.length > 0)) && (
            <div style={{ marginTop: 2, display: 'flex', gap: 0, flexWrap: 'wrap' }}>
              {item.contactTagValues && item.contactTagValues.slice(0, 3).map((t: any, idx: number) => (
                <Tag
                  key={`tag-${idx}`}
                  style={{
                    backgroundColor: '#F8FFFB',
                    color: '#0F5132',
                    fontSize: '12px',
                    padding: '0 3px',
                    borderRadius: 4,
                    border: '1px solid #A7E6C5',
                    cursor: 'default',
                    margin: 2,
                  }}
                  onMouseEnter={(e: any) => (e.currentTarget.style.backgroundColor = '#F0FFF2')}
                  onMouseLeave={(e: any) => (e.currentTarget.style.backgroundColor = '#F8FFFB')}
                >
                  {t.value}
                </Tag>
              ))}
              {item.contactTagValues && item.contactTagValues.length > 3 && (
                <Tag
                  style={{
                    backgroundColor: '#F8FFFB',
                    color: '#0F5132',
                    fontSize: '12px',
                    padding: '0 3px',
                    borderRadius: 4,
                    border: '1px solid #A7E6C5',
                    cursor: 'default',
                    margin: 2,
                  }}
                  onMouseEnter={(e: any) => (e.currentTarget.style.backgroundColor = '#F0FFF2')}
                  onMouseLeave={(e: any) => (e.currentTarget.style.backgroundColor = '#F8FFFB')}
                >
                  +{item.contactTagValues.length - 3}
                </Tag>
              )}
              {item.contactGroups && item.contactGroups.slice(0, 3).map((g: any, idx: number) => (
                <Tag
                  key={`grp-${g.id || idx}`}
                  style={{
                    backgroundColor: '#F0F5FF',
                    color: '#1D39C4',
                    fontSize: '12px',
                    padding: '0 3px',
                    borderRadius: 12,
                    border: '1px solid #ADC6FF',
                    cursor: 'default',
                    margin: 2,
                  }}
                  onMouseEnter={(e: any) => (e.currentTarget.style.backgroundColor = '#E6EDFF')}
                  onMouseLeave={(e: any) => (e.currentTarget.style.backgroundColor = '#F0F5FF')}
                >
                  {g.name}
                </Tag>
              ))}
              {item.contactGroups && item.contactGroups.length > 3 && (
                <Tag
                  style={{
                    backgroundColor: '#F0F5FF',
                    color: '#1D39C4',
                    fontSize: '12px',
                    padding: '0 3px',
                    borderRadius: 12,
                    border: '1px solid #ADC6FF',
                    cursor: 'default',
                    margin: 2,
                  }}
                  onMouseEnter={(e: any) => (e.currentTarget.style.backgroundColor = '#E6EDFF')}
                  onMouseLeave={(e: any) => (e.currentTarget.style.backgroundColor = '#F0F5FF')}
                >
                  +{item.contactGroups.length - 3}
                </Tag>
              )}
            </div>
          )}
        </div>
      }
    />
  </List.Item>
};


const mapStateToProps = (state: any) => {
  const {
    notification: { latestNotifications = [] } = {},
  } = state
  return { latestNotifications }
}

const mapDispatchToProps = (dispatch) => {
  return {
    goToContactDetails(id) {
      return () => {
        dispatch(push(`/contacts/${id}`))
      }
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MessageListItem);
